<script lang="ts">
  import { browser } from '$app/environment'
  import { goto, invalidateAll } from '$app/navigation'
  import PopupManager from '$lib/components/PopupManager.svelte'
  import { Toaster } from '$lib/components/ui/sonner'
  import { paymentDialogOpen } from '$lib/stores'
  import notifications from '$lib/stores/notifications'
  import { FONT_URLS } from '@coso/core/constants'
  import type { UserNotification } from '@coso/core/realtime/types'
  import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query'
  import { ModeWatcher } from 'mode-watcher'
  import { onMount } from 'svelte'
  import { toast } from 'svelte-sonner'
  import '../app.css'
  import type { LayoutProps } from './$types'
  let { children }: LayoutProps = $props()

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        enabled: browser,
        refetchOnWindowFocus: false,
      },
    },
  })

  let toasts = $derived(Object.values($notifications).filter((a) => a.type == 'toast'))

  async function loadFonts() {
    await document.fonts.ready
    Object.entries(FONT_URLS).forEach(async ([font, url]) => {
      const fontFace = new FontFace(font, `url(${url})`)
      await fontFace.load()
      document.fonts.add(fontFace)
    })
  }

  onMount(() => {
    loadFonts()
    return () => {
      unsubscribe()
    }
  })

  const unsubscribe = notifications.subscribe((d) => {
    const key = Object.keys(d)
      .sort((a: string, b: string) => a.localeCompare(b))
      .at(0)

    if (!key) {
      return
    }
    const item = d[key] as UserNotification

    if (item.type === 'toast') {
      let action: any | undefined
      if (item.url) {
        action = {
          label: 'Go',
          onClick: () => goto(item.url!),
        }
      }
      if (item.level === 'success') {
        toast.success(item.title, {
          description: item.message,
          duration: item.duration,
          action: action!,
        })
      }
      if (item.level === 'error') {
        toast.error(item.title, {
          description: item.message,
          duration: item.duration,
          action: action!,
        })
      }
      if (item.level === 'info') {
        toast.info(item.title, {
          description: item.message,
          duration: item.duration,
          action: action!,
        })
      }
      if (item.level === 'warning') {
        toast.warning(item.title, {
          description: item.message,
          duration: item.duration,
          action: action!,
        })
      }
    }
    if (item.action) {
      const actions = {
        invalidate: invalidateAll,
        reload: location.reload,
        payment: () => {
          $paymentDialogOpen = true
        },
      }
      actions[item.action]()
    }
  })
</script>

<svelte:head>
  <title>Coso.ai - Your Social Media Co-Pilot</title>
  <meta
    name="description"
    content="Coso.ai is your social media co-pilot. It helps you create, schedule, and manage your social media content."
  />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="theme-color" content="#000000" />
</svelte:head>
<QueryClientProvider client={queryClient}>
  <ModeWatcher defaultMode="light" track={false} />
  {@render children?.()}
</QueryClientProvider>

<Toaster />
<PopupManager />
